import { createFileRoute } from '@tanstack/react-router';
import { Building2, Calendar, MapPin, Search, Users } from 'lucide-react';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAllOrganisations } from '@/features/organisations/api/organisation-queries';

export default function AdminOrganisationsPage() {
  const { data: organisations, isLoading } = useAllOrganisations();
  const { search } = Route.useSearch();
  const navigate = Route.useNavigate();

  const filteredOrganisations = organisations.filter(
    (org) =>
      org.title.toLowerCase().includes(search.toLowerCase()) ||
      org.country.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await navigate({ search: { search: e.target.value } });
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organisations</h1>
            <p className="text-gray-600">
              View all organisations in the system
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search organisations..."
            value={search}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading organisations...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOrganisations.map((org) => (
            <Card key={org.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {org.title}
                  </CardTitle>
                  <Badge variant="outline">{org.currency}</Badge>
                </div>
                <CardDescription>Organisation ID: {org.id}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span>{org.country}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-500" />
                    <span>{org.members} members</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span>
                      Created {new Date(org.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">System:</span>
                    <Badge variant="secondary" className="text-xs">
                      {org.measurement_system}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Wastage:</span>
                    <span>{org.wastage}%</span>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    View Details
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    View Members
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && filteredOrganisations.length === 0 && (
        <div className="text-center py-8">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No organisations found
          </h3>
          <p className="text-gray-600">
            {search
              ? 'No organisations match your search criteria.'
              : 'No organisations available.'}
          </p>
        </div>
      )}
    </div>
  );
}

const adminOrganisatonsPageSearchSchema = z.object({
  search: z.string().default('').catch(''),
});

export const Route = createFileRoute('/_authenticated/admin/organisations')({
  validateSearch: adminOrganisatonsPageSearchSchema,
  component: AdminOrganisationsPage,
});
